import { Zone } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { ZoneOutline } from "@/pages/map/models/zoneContainment";

const RESULTS_LIMIT = 5000;
/**
 * Zones are drawn on a map or tested against a position, and neither changes minute to minute.
 * An hour keeps a page from asking again while somebody works.
 */
const STALE_TIME_MS = 60 * 60 * 1000;

/**
 * Every zone, with the corners needed to tell whether something is inside it.
 *
 * Fetched whole rather than per position: saying which zone each of a hundred vehicles is in
 * means the shapes have to be here anyway, and there is no call that answers it the other way
 * round.
 */
export function useZoneOutlines(enabled = true): ZoneOutline[] {
  const { geotabClient, activeSessionId } = useGeotabClient();

  const { data } = useQuery<ZoneOutline[]>({
    queryKey: ["zoneOutlines", activeSessionId],
    enabled,
    staleTime: STALE_TIME_MS,

    queryFn: async ({ signal }) => {
      const zones: Zone[] = await geotabClient.get(
        "Zone",
        { propertySelector: { fields: ["id", "name", "points"], isIncluded: true } },
        RESULTS_LIMIT,
        signal,
      );

      // Anything without a usable outline is dropped here rather than checked for on every
      // position: Geotab also stores points and lines as zones, and those have no inside.
      return (zones ?? []).flatMap(zone =>
        zone.id && (zone.points?.length ?? 0) >= 3
          ? [{ id: zone.id, name: zone.name ?? zone.id, points: zone.points as ZoneOutline["points"] }]
          : [],
      );
    },
  });

  return useMemo(() => data ?? [], [data]);
}
